import { useState, useCallback } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { usePreferences, useFormatters } from '../../../contexts/PreferencesContext';
import type { POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';
import { POLineDrilldownModal } from './POLineDrilldownModal';
import { deliveryTabCounts, type POLinePeriodProps, type POLineDrilldownState } from './types';
import { fytdLabel } from './fytdLabel';
import { SupplierFileLinesChart, SupplierFileVolumeFrame, isSupplierFileData, lineCounts } from './SupplierFileVolume';

const PALETTE = {
  cumSpend: '#0f172a',
  monthSpend: '#3b82f6',
  target: '#94a3b8',
  ncr: '#dc2626',
};

function ClickableValue({ onClick, children, title }: { onClick: () => void; children: React.ReactNode; title: string }) {
  return (
    <button
      type="button" onClick={onClick} title={title}
      className="bg-transparent border-0 p-0 m-0 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary/40 rounded"
    >
      {children}
    </button>
  );
}

/**
 * Year to Date panel of the PO Line Dashboard — clone of the Scheduled
 * Dashboard's YTD with PO-line wording and PO-line drill-downs.
 */
export function POLineYTD({ data, formatCurrency, filters, onRefresh }: POLinePeriodProps) {
  const ytd = data.ytd;
  const fromFiles = isSupplierFileData(data);
  const ytdLabel = fytdLabel(ytd.window_start);   // W3-T12: "FYTD, 1 Apr → today"
  const qualityLabel = useQualityMeasureLabel();
  const { preferences } = usePreferences();
  const { formatCompactCurrency } = useFormatters();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;

  const otifTarget = data.otif_target ?? 95;
  const otdTarget = data.otd_target ?? 95;
  const deliveryMode = data.delivery_metric_mode ?? 'both';
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only';
  const showOTD  = deliveryMode === 'both' || deliveryMode === 'otd_only';

  const ytdStart = data.per_month.months[0]?.month_start ?? data.today;
  const ytdEnd = data.today;

  const [modal, setModal] = useState<POLineDrilldownState | null>(null);
  const openYTD = useCallback((mode: POLineDrilldownMode) => {
    setModal({ mode, periodStart: ytdStart, periodEnd: ytdEnd, periodGrain: 'month', periodLabel: 'Year to date', tabCounts: deliveryTabCounts(data.ytd.delivery) });
  }, [ytdStart, ytdEnd]);
  const openMonth = useCallback((mode: POLineDrilldownMode, monthIdx: number) => {
    const m = data.per_month.months[monthIdx];
    if (!m) return;
    setModal({ mode, periodStart: m.month_start, periodEnd: m.month_end, periodGrain: 'month', periodLabel: m.label, tabCounts: deliveryTabCounts(m.delivery) });
  }, [data.per_month.months]);

  let cumLines = 0;
  const lineRows = data.per_month.months.map((m, idx) => {
    const c = lineCounts(m.delivery);
    cumLines += c.delivered;
    return { idx, label: m.label, onTime: c.onTime, late: c.late, cumulative: cumLines };
  });

  let cum = 0;
  const monthly = data.per_month.months.map((m, idx) => {
    cum += m.cost.received_value;
    const hasDeliveryData = m.delivery.total_due > 0;
    return {
      idx,
      month: m.label,
      received: m.cost.received_value,
      open: m.cost.open_value,
      committed: m.cost.total_value,
      cumReceived: cum,
      ncrs: m.quality.ncrs,
      otif: hasDeliveryData ? m.delivery.otif_pct : null,
      otd: hasDeliveryData ? m.delivery.otd_pct : null,
      target: deliveryMode === 'otd_only' ? otdTarget : otifTarget,
    };
  });

  type RechartsClickPayload = { payload?: { idx?: number }; idx?: number };
  const handleBarClick = (mode: POLineDrilldownMode) => (payload: unknown) => {
    const p = payload as RechartsClickPayload;
    const idx = p?.payload?.idx ?? p?.idx;
    if (typeof idx === 'number') openMonth(mode, idx);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle={ytdLabel}>
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label={qualityLabel}
              value={<ClickableValue onClick={() => openYTD('ppm')} title="Drill down: NCRs with reject qty (YTD)"><span className="text-2xl font-semibold tabular-nums">{ytd.quality.ppm.toLocaleString()}</span></ClickableValue>}
              foot={`${ytd.quality.reject_qty.toLocaleString()} rej`}
            />
            <KPICell
              label="NCRs"
              value={<ClickableValue onClick={() => openYTD('ncrs')} title="Drill down: all NCRs raised YTD"><span className="text-2xl font-semibold tabular-nums">{ytd.quality.ncrs.toLocaleString()}</span></ClickableValue>}
              foot="cumulative"
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={ytdLabel}>
          <div className={`grid gap-3 items-start ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={<ClickableValue onClick={() => openYTD('otif_due')} title="Drill down: YTD PO lines (OTIF view)"><CircularProgress percentage={ytd.delivery.otif_pct} size={80} strokeWidth={6} color={performanceColor(ytd.delivery.otif_pct, otifTarget)} decimals={kpiDecimals} /></ClickableValue>}
                foot={`${ytd.delivery.otif_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} PO lines`}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={<ClickableValue onClick={() => openYTD('otd_due')} title="Drill down: YTD PO lines due — On time / Not on time"><CircularProgress percentage={ytd.delivery.otd_pct} size={80} strokeWidth={6} color={performanceColor(ytd.delivery.otd_pct, otdTarget)} decimals={kpiDecimals} /></ClickableValue>}
                foot={`${ytd.delivery.otd_met.toLocaleString()}/${(ytd.delivery.otd_due ?? ytd.delivery.total_due).toLocaleString()} lines on time`}
              />
            )}
            <KPICell
              label="Appeals"
              value={<ClickableValue onClick={() => openYTD('appeals')} title="Drill down: delivery appeals YTD"><span className="text-2xl font-semibold tabular-nums">{ytd.delivery.appeals}</span></ClickableValue>}
            />
          </div>
        </SectionFrame>

        {fromFiles ? (
          <SupplierFileVolumeFrame delivery={ytd.delivery} subtitle={ytdLabel} onOpen={openYTD} />
        ) : (
        <SectionFrame title="Cost" accent="blue" subtitle="PO lines due YTD">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open PO Lines"
              value={<ClickableValue onClick={() => openYTD('open')} title="Drill down: open PO lines YTD"><span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.open_value)}</span></ClickableValue>}
              foot={`${ytd.cost.open_lines.toLocaleString()} of ${ytd.cost.total_lines.toLocaleString()} lines`}
            />
            <KPICell
              label="Received (YTD)"
              value={<ClickableValue onClick={() => openYTD('received')} title="Drill down: received PO lines YTD"><span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.received_value)}</span></ClickableValue>}
              foot={`of ${formatCurrency(ytd.cost.total_value)} committed`}
            />
            <KPICell
              label="Arrears"
              value={<ClickableValue onClick={() => openYTD('arrears')} title="Drill down: past-due open PO lines due YTD"><span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.arrears_value)}</span></ClickableValue>}
              foot={`${ytd.cost.arrears_lines.toLocaleString()} past-due lines`}
            />
          </div>
        </SectionFrame>
        )}
      </div>

      {fromFiles ? (
        <SupplierFileLinesChart
          title="Delivery lines by month — on time vs late"
          description="Bars: lines delivered that month, from the supplier files (on time / late) • Line: running total • Click a bar to drill in"
          rows={lineRows}
          showCumulative
          onBarClick={openMonth}
        />
      ) : (
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">PO Line Value by Month — Received vs Open</CardTitle>
          <p className="text-[11px] text-muted-foreground">Bars: value of PO lines due that month (split received / still open) • Line: YTD cumulative received • Click a bar to drill in</p>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatCompactCurrency(v, data.currency)} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatCompactCurrency(v, data.currency)} />
                <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                <Bar yAxisId="left" dataKey="received" stackId="cost" fill={PALETTE.monthSpend} name="Received" radius={[0, 0, 0, 0]} onClick={handleBarClick('received')} style={{ cursor: 'pointer' }} activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} />
                <Bar yAxisId="left" dataKey="open" stackId="cost" fill="#cbd5e1" name="Open" radius={[3, 3, 0, 0]} onClick={handleBarClick('open')} style={{ cursor: 'pointer' }} activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} />
                <Line yAxisId="right" type="monotone" dataKey="cumReceived" stroke={PALETTE.cumSpend} strokeWidth={2.5} dot={{ r: 3 }} name="Cumulative received" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}

      <div className={`grid gap-4 ${(showOTIF || showOTD) ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {(showOTIF || showOTD) && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">
                Delivery — {showOTIF && showOTD ? 'OTIF / OTD' : showOTIF ? 'OTIF' : 'OTD'} by Month
              </CardTitle>
              <p className="text-[11px] text-muted-foreground">Target {monthly[0]?.target ?? 95}% (dashed) • Click a point to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                    <ReferenceLine y={monthly[0]?.target ?? 95} stroke={PALETTE.target} strokeDasharray="4 4" />
                    {showOTIF && (
                      <Line type="monotone" dataKey="otif" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="OTIF" connectNulls={false}
                            activeDot={{ r: 5, onClick: (_e: unknown, p: unknown) => handleBarClick('otif_due')(p), style: { cursor: 'pointer' } }} />
                    )}
                    {showOTD && (
                      <Line type="monotone" dataKey="otd" stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3 }} name="OTD" connectNulls={false}
                            activeDot={{ r: 5, onClick: (_e: unknown, p: unknown) => handleBarClick('otd_due')(p), style: { cursor: 'pointer' } }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">NCRs — Monthly</CardTitle>
            <p className="text-[11px] text-muted-foreground">Issues raised against suppliers • Click a bar to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar dataKey="ncrs" fill={PALETTE.ncr} name="NCRs" radius={[3, 3, 0, 0]} onClick={handleBarClick('ncrs')} style={{ cursor: 'pointer' }} activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {modal && (
        <POLineDrilldownModal
          open
          onClose={() => setModal(null)}
          mode={modal.mode}
          filters={filters}
          periodStart={modal.periodStart}
          periodEnd={modal.periodEnd}
          periodGrain={modal.periodGrain}
          periodLabel={modal.periodLabel}
          currency={data.currency}
          tabCounts={modal.tabCounts}
          tabContext={modal.tabContext}
          onDataChanged={onRefresh}
        />
      )}
    </div>
  );
}
